/**
 * CreateTaskDialog — two-step wizard
 *
 * Step 1: site + optional sub-site, workflow template, title
 * Step 2: assignee, priority, due date, optional contract
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Typography, CircularProgress, Alert, Chip, Stepper,
  Step, StepLabel, Autocomplete, Divider, Paper,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Plus, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import { useOrg } from '../../contexts/OrgContext';
import useAssignableUsers, { filterVendorCrewByProvider, renderAssignableMenuItems } from '../../hooks/useAssignableUsers';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

const CreateTaskDialog = ({ open, onClose, onCreated, prefillSiteId, prefillSubSiteId, restrictAssetTypeId }) => {
  const { selectedOrgId } = useOrg();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Data fetches
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [subSites, setSubSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Step 1
  const [siteId, setSiteId] = useState(prefillSiteId || '');
  const [subSiteId, setSubSiteId] = useState(prefillSubSiteId || '');
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Step 2
  const [assignedTo, setAssignedTo] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [contractId, setContractId] = useState('');

  const getToken = async () => {
    const user = getAuth().currentUser;
    return user ? await user.getIdToken() : null;
  };

  // Re-seed the prefilled site/sub-site each time the dialog opens. The
  // useState initializers only run on first mount — which happens before the
  // parent page finishes auto-selecting a site — so without this the dialog
  // keeps a stale (or empty) siteId even after the prefill prop updates.
  useEffect(() => {
    if (!open) return;
    if (prefillSiteId) setSiteId(prefillSiteId);
    if (prefillSubSiteId) setSubSiteId(prefillSubSiteId);
  }, [open, prefillSiteId, prefillSubSiteId]);

  // Load sites
  useEffect(() => {
    if (!open || !selectedOrgId) return;
    setLoadingSites(true);
    getToken().then(token =>
      axios.get(`${BASE_URL}/api/sites?organisation_id=${selectedOrgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).then(res => setSites(res.data || []))
      .catch(console.error)
      .finally(() => setLoadingSites(false));
  }, [open, selectedOrgId]);

  // Load workflow templates (optionally scoped to selected site's asset type)
  useEffect(() => {
    if (!open || !selectedOrgId) return;
    setLoadingTemplates(true);
    const site = sites.find(s => s.site_id === siteId);
    const assetTypeId = site?.asset_type_id || restrictAssetTypeId;
    const params = new URLSearchParams({ organisation_id: selectedOrgId });
    if (assetTypeId) params.set('asset_type_id', String(assetTypeId));
    getToken().then(token =>
      axios.get(`${BASE_URL}/api/workflow-templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).then(res => setTemplates(res.data || []))
      .catch(console.error)
      .finally(() => setLoadingTemplates(false));
  }, [open, selectedOrgId, siteId, sites, restrictAssetTypeId]);

  // Clear workflow if it no longer matches the selected site asset type
  useEffect(() => {
    if (!templateId || !siteId) return;
    const site = sites.find(s => s.site_id === siteId);
    const tpl = templates.find(t => t.id === templateId);
    if (site && tpl?.asset_type_id && tpl.asset_type_id !== site.asset_type_id) {
      setTemplateId('');
    }
  }, [siteId, sites, templates, templateId]);

  // Grouped assignee options (internal staff + vendor crew)
  const { staff, vendorCrew } = useAssignableUsers(open ? selectedOrgId : null);

  // Load contracts
  useEffect(() => {
    if (!open || !selectedOrgId) return;
    getToken().then(token =>
      axios.get(`${BASE_URL}/api/contracts?organisation_id=${selectedOrgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).then(res => setContracts(res.data || []))
      .catch(console.error);
  }, [open, selectedOrgId]);

  // Load sub-sites when site changes
  useEffect(() => {
    if (!siteId) { setSubSites([]); return; }
    getToken().then(token =>
      axios.get(`${BASE_URL}/api/sub-sites?site_id=${siteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).then(res => setSubSites(res.data || []))
      .catch(() => setSubSites([]));
  }, [siteId]);

  // Auto-generate title from site + workflow
  useEffect(() => {
    if (!title) {
      const siteName = sites.find(s => s.site_id === siteId)?.name;
      const templateName = templates.find(t => t.id === templateId)?.name;
      if (siteName && templateName) setTitle(`${templateName} — ${siteName}`);
    }
  }, [siteId, templateId]);

  const selectedTemplate = templates.find(t => t.id === templateId);
  const selectedSite = sites.find(s => s.site_id === siteId);
  const feasibilityNotApproved = selectedSite && selectedSite.feasibility_status && selectedSite.feasibility_status !== 'approved';

  // When a contract is selected, scope the vendor-crew group to that contract's vendor.
  const selectedContract = contracts.find(c => c.id === contractId);
  const filteredVendorCrew = filterVendorCrewByProvider(vendorCrew, selectedContract?.service_provider_id);

  const handleNext = () => {
    setError('');
    if (step === 0) {
      if (!siteId) { setError('Please select a site.'); return; }
      if (!templateId) { setError('Please select a workflow.'); return; }
      if (!title.trim()) { setError('Please enter a task title.'); return; }
    }
    setStep(1);
  };

  const handleCreate = async () => {
    setError('');
    setCreating(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${BASE_URL}/api/tasks`, {
        organisation_id: selectedOrgId,
        site_id: siteId,
        sub_site_id: subSiteId || undefined,
        workflow_template_id: templateId,
        title,
        description: description || undefined,
        assigned_to: assignedTo || undefined,
        priority,
        due_date: dueDate || undefined,
        contract_id: contractId || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });

      onCreated?.(res.data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSiteId(prefillSiteId || '');
    setSubSiteId(prefillSubSiteId || '');
    setTemplateId('');
    setTitle('');
    setDescription('');
    setAssignedTo('');
    setPriority('medium');
    setDueDate('');
    setContractId('');
    setError('');
    onClose();
  };

  const myTemplates = templates.filter(t => t.organisation_id === selectedOrgId);
  const publicTemplates = templates.filter(t => !t.organisation_id);
  const siteAssetLabel = selectedSite?.asset_type;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create New Task
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {step === 0 ? 'Step 1 — What & where' : 'Step 2 — Assignment & scheduling'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>What & where</StepLabel></Step>
          <Step><StepLabel>Assignment</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Site */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Site</InputLabel>
              <Select
                value={siteId}
                label="Site"
                onChange={e => { setSiteId(e.target.value); setSubSiteId(''); }}
                disabled={loadingSites || !!prefillSiteId}
              >
                {loadingSites && <MenuItem disabled><CircularProgress size={14} /></MenuItem>}
                {sites.map(s => (
                  <MenuItem key={s.site_id} value={s.site_id}>
                    {s.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({s.asset_type})</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Feasibility warning */}
            {feasibilityNotApproved && (
              <Alert severity="info" icon={<AlertCircle size={16} />} sx={{ py: 0.5 }}>
                Site feasibility not yet approved ({selectedSite.feasibility_status || 'not assessed'}) — you can still create a task.
              </Alert>
            )}

            {/* Sub-site */}
            {(subSites.length > 0 || prefillSubSiteId) && (
              <FormControl fullWidth size="small">
                <InputLabel>Plot / Sub-site (optional)</InputLabel>
                <Select
                  value={subSiteId}
                  label="Plot / Sub-site (optional)"
                  onChange={e => setSubSiteId(e.target.value)}
                  disabled={!!prefillSubSiteId}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {subSites.map(s => (
                    <MenuItem key={s.sub_site_id} value={s.sub_site_id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Workflow */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Workflow</InputLabel>
              <Select
                value={templateId}
                label="Workflow"
                onChange={e => setTemplateId(e.target.value)}
                disabled={loadingTemplates}
              >
                {loadingTemplates && <MenuItem disabled><CircularProgress size={14} /></MenuItem>}
                {myTemplates.length > 0 && [
                  <MenuItem key="_my_header" disabled sx={{ fontWeight: 600, fontSize: 12, opacity: 0.7 }}>MY WORKFLOWS</MenuItem>,
                  ...myTemplates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      {t.asset_type_name && (
                        <Chip label={t.asset_type_name} size="small" sx={{ ml: 1 }} variant="outlined" />
                      )}
                    </MenuItem>
                  )),
                ]}
                {publicTemplates.length > 0 && [
                  <MenuItem key="_pub_header" disabled sx={{ fontWeight: 600, fontSize: 12, opacity: 0.7, mt: 1 }}>PUBLIC TEMPLATES</MenuItem>,
                  ...publicTemplates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      <Chip label="Public" size="small" color="info" sx={{ ml: 1 }} />
                      {t.asset_type_name && (
                        <Chip label={t.asset_type_name} size="small" sx={{ ml: 0.5 }} variant="outlined" />
                      )}
                    </MenuItem>
                  )),
                ]}
                {!loadingTemplates && myTemplates.length === 0 && publicTemplates.length === 0 && siteAssetLabel && (
                  <MenuItem disabled>No workflows for {siteAssetLabel} sites</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Stage preview — horizontal chips */}
            {selectedTemplate && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Workflow stages ({selectedTemplate.stage_count || (selectedTemplate.stage_names || []).length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {(selectedTemplate.stage_names || []).length > 0
                    ? (selectedTemplate.stage_names || []).map((s, i) => (
                        <React.Fragment key={i}>
                          <Chip
                            label={s.name}
                            size="small"
                            variant={i === 0 ? 'filled' : 'outlined'}
                            color={i === 0 ? 'primary' : 'default'}
                            sx={{ fontSize: '0.72rem', height: 22 }}
                          />
                          {i < (selectedTemplate.stage_names || []).length - 1 && (
                            <ChevronRight size={12} color="#9ca3af" style={{ flexShrink: 0 }} />
                          )}
                        </React.Fragment>
                      ))
                    : <Typography variant="caption" color="text.secondary">{selectedTemplate.stage_count || '?'} stages</Typography>
                  }
                </Box>
              </Paper>
            )}

            {/* Title */}
            <TextField
              label="Task title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              fullWidth
              size="small"
            />

            <TextField
              label="Description (optional)"
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Assignee */}
            <FormControl fullWidth size="small">
              <InputLabel>Assigned to (optional)</InputLabel>
              <Select value={assignedTo} label="Assigned to (optional)" onChange={e => setAssignedTo(e.target.value)}>
                <MenuItem value=""><em>Unassigned</em></MenuItem>
                {renderAssignableMenuItems(staff, filteredVendorCrew)}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={priority} label="Priority" onChange={e => setPriority(e.target.value)}>
                {PRIORITIES.map(p => (
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Due date */}
            <TextField
              label="Due date (optional)"
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* Contract */}
            <FormControl fullWidth size="small">
              <InputLabel>Contract (optional)</InputLabel>
              <Select value={contractId} label="Contract (optional)" onChange={e => setContractId(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {contracts.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 1 && (
          <Button startIcon={<ChevronLeft size={14} />} onClick={() => setStep(0)}>
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>Cancel</Button>
        {step === 0 ? (
          <Button variant="contained" endIcon={<ChevronRight size={14} />} onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant="contained" startIcon={creating ? <CircularProgress size={14} /> : <Plus size={14} />}
            onClick={handleCreate} disabled={creating}>
            Create Task
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskDialog;
